import { memo, useState, useCallback } from 'react';
import { Smile, ChevronDown, Reply, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmojiReactionPicker } from '@/components/messages/EmojiReactionPicker';
import type { IMessageActionsProps } from '@/@types/messages';

const ActionToolbar = memo(function ActionToolbar({
  onReaction,
  onReply,
  onDeleteForMe,
  onDeleteForEveryone,
  isOutgoing,
}: {
  // LOCAL_TYPE_JUSTIFIED: internal-only props for extracted sub-component, not reusable outside this file
  onReaction: (emoji: string) => void;
  onReply: () => void;
  onDeleteForMe: () => void;
  onDeleteForEveryone: () => void;
  isOutgoing: boolean;
}) {
  const [emojiOpen, setEmojiOpen] = useState(false);

  const handleReaction = useCallback(
    (emoji: string) => {
      onReaction(emoji);
      setEmojiOpen(false);
    },
    [onReaction]
  );

  return (
    <div
      aria-hidden="true"
      className={cn(
        'flex items-center gap-0.5 rounded-md bg-popover border border-border shadow-sm px-1 py-0.5',
        'opacity-0 group-hover:opacity-100 transition-opacity z-20 shrink-0 self-start mt-1'
      )}
    >
      <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'rounded p-1 text-muted-foreground',
              'hover:bg-accent hover:text-foreground transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
            aria-label="React to message"
          >
            <Smile className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" side="top" align="start">
          <EmojiReactionPicker onSelect={handleReaction} />
        </PopoverContent>
      </Popover>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'rounded p-1 text-muted-foreground',
              'hover:bg-accent hover:text-foreground transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
            aria-label="Message options"
          >
            <ChevronDown className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onReply}>
            <Reply className="mr-2 size-4" />
            Reply
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Trash2 className="mr-2 size-4 text-destructive" />
              <span className="text-destructive">Delete</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={onDeleteForMe}>Delete for me</DropdownMenuItem>
              {isOutgoing && (
                <DropdownMenuItem
                  onClick={onDeleteForEveryone}
                  className="text-destructive focus-visible:text-destructive"
                >
                  Delete for everyone
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});

export const WhatsAppMessageActions = memo(function WhatsAppMessageActions({
  message,
  onReaction,
  onDeleteMessage,
  onReplyMessage,
  children,
}: IMessageActionsProps) {
  const handleReaction = useCallback(
    (emoji: string) => {
      if (message.platformMessageId !== undefined && onReaction) {
        onReaction(message.platformMessageId, emoji);
      }
    },
    [message.platformMessageId, onReaction]
  );

  const handleReply = useCallback(() => {
    onReplyMessage?.(message);
  }, [message, onReplyMessage]);

  const handleDeleteForMe = useCallback(() => {
    if (message.platformMessageId !== undefined && onDeleteMessage) {
      onDeleteMessage(message.platformMessageId, false);
    }
  }, [message.platformMessageId, onDeleteMessage]);

  const handleDeleteForEveryone = useCallback(() => {
    if (message.platformMessageId !== undefined && onDeleteMessage) {
      onDeleteMessage(message.platformMessageId, true);
    }
  }, [message.platformMessageId, onDeleteMessage]);

  const toolbar = (
    <ActionToolbar
      onReaction={handleReaction}
      onReply={handleReply}
      onDeleteForMe={handleDeleteForMe}
      onDeleteForEveryone={handleDeleteForEveryone}
      isOutgoing={message.isOutgoing ?? false}
    />
  );

  return (
    <div
      className={cn(
        'group flex items-start gap-1',
        message.isOutgoing ? 'flex-row-reverse ml-auto [&_[role=article]]:ml-0' : 'flex-row'
      )}
    >
      <div className="contents">{children}</div>
      {toolbar}
    </div>
  );
});
